/**
 * CinematicHero
 *
 * Apple-style scroll-driven product hero for Sip Theory Beverages.
 *
 * Sprint 2 additions over Sprint 1:
 *   - Mobile-first overlay layout: on portrait viewports the editorial
 *     block stacks vertically and anchors to the bottom of the frame,
 *     with reduced font sizes and condensed spacing so nothing collides
 *     with the bottle on narrow screens.
 *   - Parallax depth on overlay text: the headline and sub-copy shift
 *     vertically at different rates as the user scrolls, creating a
 *     convincing depth-of-field feel without any 3D transforms.
 *   - Duplicate scroll listener removed: scrollProgress and parallaxOffset
 *     now come directly from useScrollCanvas (single source of truth).
 *   - Loader display:none after fade: loaderVisible from the hook drives
 *     a style switch that removes the loader from the GPU compositor
 *     stacking context once its opacity transition ends, freeing a
 *     composited layer for the remainder of the page session.
 *   - Pill attribute strip on mobile collapses to two items to prevent
 *     overflow on 320px viewports.
 *
 * Sprint 3 mobile fix:
 *   - Outer section height changed from `${scrollMultiplier * 100}vh` to
 *     `calc(var(--vh, 1vh) * 100 * ${scrollMultiplier})`.
 *   - Inner sticky panel height changed from `100vh` to
 *     `calc(var(--vh, 1vh) * 100)`.
 *   - --vh is written by useScrollCanvas via updateVhProperty() and equals
 *     visualViewport.height * 0.01 — the true visible height on mobile
 *     regardless of whether the browser address bar is shown or hidden.
 *   - This eliminates the black gap above the canvas that appeared on
 *     iOS Safari and Android Chrome in mobile / desktop-view-on-mobile.
 *
 * Architecture:
 *   ┌─ containerRef ──────────────────────────────────────────────┐
 *   │  height: var(--vh)*100 × SCROLL_MULTIPLIER                  │
 *   │                                                              │
 *   │  ┌─ sticky viewport ─────────────────────────────────────┐  │
 *   │  │  height: var(--vh)*100                                 │  │
 *   │  │  <canvas />          ← frame sequence                  │  │
 *   │  │  <GradientVignette/> ← top/bottom/left fades           │  │
 *   │  │  <LoaderOverlay />   ← fades then removes from DOM     │  │
 *   │  │  <ProgressBar />     ← 1px gold scrub indicator        │  │
 *   │  │  <HeroOverlay />     ← editorial + CTAs + parallax     │  │
 *   │  │  <ScrollCue />       ← bouncing chevron                │  │
 *   │  └────────────────────────────────────────────────────────┘  │
 *   └──────────────────────────────────────────────────────────────┘
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useScrollCanvas } from '@/hooks/useScrollCanvas';

// ─── Design tokens (inline — no Tailwind class needed for dynamic values) ─────

const BRAND = {
  ink:      '#070605',
  bone50:   '#faf6ee',
  bone100:  '#f4ecdc',
  bone200:  'rgba(228,220,193,0.75)',
  gold:     '#c9a961',
  goldRgb:  '201,169,97',
  sage300:  '#aab87c',
  sageRgb:  '170,184,124',
};

// ─── Keyframe injection ───────────────────────────────────────────────────────

const KEYFRAMES = `
  @keyframes sipFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes sipFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes sipScrollBounce {
    0%, 100% { transform: translateX(-50%) translateY(0);   opacity: 0.5; }
    50%       { transform: translateX(-50%) translateY(6px); opacity: 1;   }
  }
  @media (prefers-reduced-motion: reduce) {
    @keyframes sipFadeUp        { from { opacity: 1; transform: none; } }
    @keyframes sipFadeIn        { from { opacity: 1; } }
    @keyframes sipScrollBounce  { 0%, 100% { transform: translateX(-50%); } }
  }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function LoaderOverlay({ loadProgress, isReady, loaderVisible }) {
  const percent = Math.round(loadProgress * 100);
  if (!loaderVisible) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position:       'absolute',
        inset:          0,
        zIndex:         30,
        backgroundColor: BRAND.ink,
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        gap:            '2rem',
        opacity:        isReady ? 0 : 1,
        pointerEvents:  isReady ? 'none' : 'auto',
        transition:     'opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
        willChange:     'opacity',
      }}
    >
      <div style={{ textAlign: 'center', lineHeight: 1 }}>
        <div
          style={{
            fontFamily:    "'Fraunces', 'Cormorant Garamond', serif",
            fontStyle:     'italic',
            fontSize:      'clamp(1.5rem, 3vw, 2.25rem)',
            color:         BRAND.bone50,
            letterSpacing: '-0.01em',
          }}
        >
          sip theory
        </div>
        <div
          style={{
            fontSize:      '0.6rem',
            letterSpacing: '0.42em',
            color:         'rgba(212,196,160,0.6)',
            marginTop:     '0.35rem',
            textTransform: 'uppercase',
            fontFamily:    "'Manrope', system-ui, sans-serif",
          }}
        >
          beverages
        </div>
      </div>

      <div
        style={{
          width:           'clamp(120px, 20vw, 200px)',
          height:          '1px',
          backgroundColor: 'rgba(255,255,255,0.06)',
          borderRadius:    '1px',
          overflow:        'hidden',
        }}
      >
        <div
          style={{
            height:          '100%',
            width:           `${percent}%`,
            backgroundColor: BRAND.gold,
            borderRadius:    '1px',
            transition:      'width 0.12s linear',
            willChange:      'width',
          }}
        />
      </div>

      <div
        style={{
          fontFamily:       "'Manrope', system-ui, sans-serif",
          fontSize:         '0.65rem',
          letterSpacing:    '0.28em',
          color:            'rgba(212,196,160,0.45)',
          textTransform:    'uppercase',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {percent}%
      </div>
    </div>
  );
}

function ProgressBar({ progress }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position:        'absolute',
        top:             0,
        left:            0,
        right:           0,
        height:          '1px',
        zIndex:          25,
        backgroundColor: `rgba(${BRAND.goldRgb},0.08)`,
      }}
    >
      <div
        style={{
          height:     '100%',
          width:      `${progress * 100}%`,
          background: `linear-gradient(90deg,
            rgba(${BRAND.goldRgb},0.3),
            rgba(${BRAND.goldRgb},0.9),
            rgba(${BRAND.goldRgb},0.3))`,
          transition: 'width 0.05s linear',
          willChange: 'width',
        }}
      />
    </div>
  );
}

function ScrollCue({ visible }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position:      'absolute',
        bottom:        '2.5rem',
        left:          '50%',
        transform:     'translateX(-50%)',
        zIndex:        20,
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           '0.5rem',
        opacity:       visible ? 1 : 0,
        transition:    'opacity 0.6s ease',
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          fontSize:      '0.55rem',
          letterSpacing: '0.36em',
          textTransform: 'uppercase',
          color:         'rgba(212,196,160,0.45)',
          fontFamily:    "'Manrope', system-ui, sans-serif",
        }}
      >
        scroll
      </span>
      <ChevronDown
        style={{
          width:     '1rem',
          height:    '1rem',
          color:     `rgba(${BRAND.goldRgb},0.5)`,
          animation: 'sipScrollBounce 2s ease-in-out infinite',
        }}
      />
    </div>
  );
}

function AttributePill({ label, hidden }) {
  return (
    <span
      style={{
        display:          hidden ? 'none' : 'inline-flex',
        alignItems:       'center',
        gap:              '0.4rem',
        padding:          '0.35rem 0.85rem',
        borderRadius:     '999px',
        border:           '1px solid rgba(244,236,220,0.1)',
        backgroundColor:  'rgba(255,255,255,0.03)',
        backdropFilter:   'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        fontSize:         '0.65rem',
        letterSpacing:    '0.12em',
        textTransform:    'uppercase',
        color:            'rgba(212,196,160,0.8)',
        fontFamily:       "'Manrope', system-ui, sans-serif",
        fontWeight:       500,
        whiteSpace:       'nowrap',
      }}
    >
      <span
        style={{
          width:           '0.35rem',
          height:          '0.35rem',
          borderRadius:    '50%',
          backgroundColor: BRAND.gold,
          flexShrink:      0,
        }}
      />
      {label}
    </span>
  );
}

const PARALLAX_HEADLINE_PX = 20;
const PARALLAX_COPY_PX     = 10;

const ATTRIBUTES = [
  { label: 'Real Ingredients',    mobileHide: false },
  { label: 'No Added Sugar',      mobileHide: false },
  { label: 'Refrigerated Fresh',  mobileHide: true  },
];

function HeroOverlay({ isReady, scrollProgress, parallaxOffset, isMobile }) {
  const overlayOpacity = useMemo(() => {
    if (!isReady) return 0;
    if (scrollProgress < 0.6) return 1;
    const t = (scrollProgress - 0.6) / 0.3;
    return Math.max(0, 1 - t);
  }, [isReady, scrollProgress]);

  const headlineShift = parallaxOffset * -PARALLAX_HEADLINE_PX;
  const copyShift     = parallaxOffset * -PARALLAX_COPY_PX;

  const fadeUp = (delay) =>
    isReady
      ? `sipFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s both`
      : 'none';

  return (
    <div
      style={{
        position:       'absolute',
        inset:          0,
        zIndex:         15,
        pointerEvents:  'none',
        display:        'flex',
        flexDirection:  'column',
        justifyContent: 'flex-end',
        paddingBottom:  isMobile ? '5rem' : 'clamp(3rem, 7vh, 5rem)',
        paddingLeft:    isMobile ? '1.25rem' : 'clamp(1.5rem, 5vw, 5rem)',
        paddingRight:   isMobile ? '1.25rem' : 'clamp(1.5rem, 5vw, 5rem)',
        opacity:        overlayOpacity,
        transition:     'opacity 0.4s ease',
        willChange:     'opacity',
      }}
    >
      <div
        style={{
          maxWidth:      isMobile ? '100%' : 'clamp(280px, 42vw, 560px)',
          display:       'flex',
          flexDirection: 'column',
          gap:           isMobile ? '0.9rem' : '1.25rem',
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            display:    'flex',
            alignItems: 'center',
            gap:        '0.75rem',
            animation:  fadeUp(0.1),
          }}
        >
          <span
            style={{
              display:         'inline-block',
              width:           '1.75rem',
              height:          '1px',
              backgroundColor: BRAND.gold,
              opacity:         0.7,
              flexShrink:      0,
            }}
          />
          <span
            style={{
              fontSize:      '0.65rem',
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color:         `rgba(${BRAND.goldRgb},0.85)`,
              fontFamily:    "'Manrope', system-ui, sans-serif",
              fontWeight:    500,
            }}
          >
            Oreo Cold Coffee
          </span>
        </div>

        {/* Headline */}
        <div
          style={{
            transform:  `translateY(${headlineShift}px)`,
            willChange: 'transform',
            animation:  fadeUp(0.22),
          }}
        >
          <h1
            style={{
              margin:        0,
              fontFamily:    "'Fraunces', 'Cormorant Garamond', serif",
              fontWeight:    400,
              fontSize:      isMobile
                ? 'clamp(2.25rem, 10vw, 3rem)'
                : 'clamp(2.5rem, 6vw, 5.5rem)',
              lineHeight:    1.01,
              letterSpacing: '-0.02em',
              color:         BRAND.bone50,
              textWrap:      'balance',
            }}
          >
            Goodness
            <br />
            <em style={{ color: BRAND.sage300, fontStyle: 'italic' }}>
              in every sip.
            </em>
          </h1>
        </div>

        {/* Sub-copy (hidden on mobile) */}
        {!isMobile && (
          <div
            style={{
              transform:  `translateY(${copyShift}px)`,
              willChange: 'transform',
              animation:  fadeUp(0.34),
            }}
          >
            <p
              style={{
                margin:     0,
                fontFamily: "'Manrope', system-ui, sans-serif",
                fontSize:   'clamp(0.8rem, 1.2vw, 0.95rem)',
                lineHeight: 1.65,
                color:      BRAND.bone200,
                maxWidth:   '36ch',
                textWrap:   'pretty',
              }}
            >
              Real Oreo. Real cold brew. No added sugar,
              no artificial anything — just refrigerated freshness
              in every bottle.
            </p>
          </div>
        )}

        {/* Attribute pills */}
        <div
          style={{
            display:   'flex',
            flexWrap:  'wrap',
            gap:       '0.5rem',
            animation: fadeUp(0.44),
          }}
        >
          {ATTRIBUTES.map(({ label, mobileHide }) => (
            <AttributePill
              key={label}
              label={label}
              hidden={isMobile && mobileHide}
            />
          ))}
        </div>

        {/* CTAs */}
        <div
          style={{
            display:       'flex',
            flexWrap:      'wrap',
            gap:           '0.75rem',
            pointerEvents: 'auto',
            animation:     fadeUp(0.54),
          }}
        >
          <Link
            to="/shop?cat=cold-coffee"
            data-testid="hero-cta-explore"
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              gap:            '0.5rem',
              padding:        isMobile ? '0.75rem 1.4rem' : '0.85rem 1.75rem',
              borderRadius:   '999px',
              background:     'linear-gradient(180deg, #c4cf9d, #7a8a4f)',
              color:          BRAND.ink,
              fontFamily:     "'Manrope', system-ui, sans-serif",
              fontWeight:     600,
              fontSize:       isMobile ? '0.68rem' : '0.72rem',
              letterSpacing:  '0.16em',
              textTransform:  'uppercase',
              textDecoration: 'none',
              boxShadow:      `0 10px 30px -10px rgba(${BRAND.sageRgb},0.55),
                               inset 0 1px 0 rgba(255,255,255,0.25)`,
              transition:     'transform 0.25s ease, box-shadow 0.25s ease',
              willChange:     'transform',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `
                0 0 60px -10px rgba(${BRAND.sageRgb},0.35),
                0 10px 30px -10px rgba(${BRAND.sageRgb},0.55),
                inset 0 1px 0 rgba(255,255,255,0.25)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `
                0 10px 30px -10px rgba(${BRAND.sageRgb},0.55),
                inset 0 1px 0 rgba(255,255,255,0.25)`;
            }}
          >
            Explore Products
            <ArrowRight style={{ width: '0.85rem', height: '0.85rem' }} />
          </Link>

          <Link
            to="/our-story"
            data-testid="hero-cta-story"
            style={{
              display:         'inline-flex',
              alignItems:      'center',
              gap:             '0.5rem',
              padding:         isMobile ? '0.75rem 1.4rem' : '0.85rem 1.75rem',
              borderRadius:    '999px',
              border:          '1px solid rgba(244,236,220,0.15)',
              color:           BRAND.bone100,
              fontFamily:      "'Manrope', system-ui, sans-serif",
              fontWeight:      500,
              fontSize:        isMobile ? '0.68rem' : '0.72rem',
              letterSpacing:   '0.16em',
              textTransform:   'uppercase',
              textDecoration:  'none',
              backdropFilter:  'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              backgroundColor: 'rgba(255,255,255,0.02)',
              transition:      'background-color 0.25s ease, border-color 0.25s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
              e.currentTarget.style.borderColor     = `rgba(${BRAND.goldRgb},0.4)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)';
              e.currentTarget.style.borderColor     = 'rgba(244,236,220,0.15)';
            }}
          >
            Our Story
          </Link>
        </div>
      </div>
    </div>
  );
}

function GradientVignette() {
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position:      'absolute',
          top: 0, left: 0, right: 0,
          height:        '14rem',
          background:    `linear-gradient(180deg,
            rgba(7,6,5,0.75) 0%,
            rgba(7,6,5,0.3)  50%,
            transparent      100%)`,
          zIndex:        10,
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position:      'absolute',
          bottom: 0, left: 0, right: 0,
          height:        '10rem',
          background:    `linear-gradient(0deg,
            ${BRAND.ink}       0%,
            rgba(7,6,5,0.6)   60%,
            transparent        100%)`,
          zIndex:        10,
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position:      'absolute',
          top: 0, left: 0, bottom: 0,
          width:         '55%',
          background:    `linear-gradient(90deg,
            rgba(7,6,5,0.65) 0%,
            rgba(7,6,5,0.25) 60%,
            transparent      100%)`,
          zIndex:        10,
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CinematicHero() {
  const {
    canvasRef,
    containerRef,
    loadProgress,
    isReady,
    loaderVisible,
    scrollProgress,
    parallaxOffset,
    scrollMultiplier,
  } = useScrollCanvas();

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const showScrollCue = isReady && scrollProgress < 0.05;

  return (
    <>
      <style>{KEYFRAMES}</style>

      {/*
        Scroll container.
        -mt-20 lg:-mt-24 cancels Layout's pt-20 / pt-24 on <main>
        so the canvas starts flush at the top of the page under the navbar.

        MOBILE FIX: height uses calc(var(--vh, 1vh) * 100) instead of 100vh.
        On mobile, 100vh includes the browser chrome (address bar height),
        making the section taller than the visible screen and creating a
        black gap above the canvas. --vh is written by useScrollCanvas hook
        via updateVhProperty() using visualViewport.height which is always
        equal to the true visible area regardless of browser chrome state.

        XL/4K FIX: position:relative + width:100vw + negative margin trick
        ensures the hero always bleeds full viewport width regardless of any
        max-width container wrapping it. This eliminates black side bars on
        screens wider than 1440px.
      */}
      <section
        ref={containerRef}
        data-testid="hero-section"
        className="-mt-20 lg:-mt-24"
        style={{
          position:        'relative',
          width:           '100vw',
          left:            '50%',
          right:           '50%',
          marginLeft:      '-50vw',
          marginRight:     '-50vw',
          height:          prefersReducedMotion
            ? 'calc(var(--vh, 1vh) * 100)'
            : `calc(var(--vh, 1vh) * 100 * ${scrollMultiplier})`,
          backgroundColor: BRAND.ink,
        }}
      >
        {/*
          Sticky viewport — pinned while the user scrolls through the container.

          MOBILE FIX: height uses calc(var(--vh, 1vh) * 100) instead of 100vh.
          Same reason as the outer section — 100vh is wrong on mobile and
          causes the sticky panel to be taller than the viewport, which pushes
          the canvas down and creates the visible black gap at the top.

          XL/4K FIX: width:100vw matches the outer section's full bleed width.
        */}
        <div
          style={{
            position:        'sticky',
            top:             0,
            width:           '100vw',
            height:          'calc(var(--vh, 1vh) * 100)',
            overflow:        'hidden',
            backgroundColor: BRAND.ink,
          }}
        >
          {/* z-index 0 — canvas, frame painting */}
          <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
              display:  'block',
              position: 'absolute',
              top:      0,
              left:     0,
              zIndex:   0,
            }}
          />

          {/* z-index 10 — gradient overlays */}
          <GradientVignette />

          {/* z-index 25 — scrub progress bar */}
          <ProgressBar progress={scrollProgress} />

          {/* z-index 30 — loader (conditionally rendered by loaderVisible) */}
          <LoaderOverlay
            loadProgress={loadProgress}
            isReady={isReady}
            loaderVisible={loaderVisible}
          />

          {/* z-index 15 — editorial overlay with parallax */}
          <HeroOverlay
            isReady={isReady}
            scrollProgress={scrollProgress}
            parallaxOffset={parallaxOffset}
            isMobile={isMobile}
          />

          {/* z-index 20 — scroll cue */}
          <ScrollCue visible={showScrollCue} />
        </div>
      </section>
    </>
  );
}
